// frontend/src/pages/TimeTracking/ReportGeneratorSingle.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Form, Select, Button, Typography, Row, Col, Space,
  message, Spin, Divider, Table, Tag, Descriptions, Empty,
  Alert, Modal, Tooltip
} from 'antd';
import {
  FileTextOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  EyeOutlined,
  RollbackOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import moment from 'moment-timezone';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useNotificationApi } from '../../context/NotificationContext';
import './TimeTrackingForm.css';

const { Title, Text } = Typography;
const { Option } = Select;

// Helper function to get file prefix from ticket type short code
const getFilePrefix = (ticketTypeShortCode) => {
    return ticketTypeShortCode || 'REP';
};

const ReportGeneratorSingle = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const notificationApi = useNotificationApi();
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [contracts, setContracts] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [crList, setCrList] = useState([]);
    const [reportData, setReportData] = useState(null);
    const [selectedContract, setSelectedContract] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [exporting, setExporting] = useState(false);

    // Notification helpers
    const showError = useCallback((title, content, key = 'reportError') => {
        notificationApi.destroy(key);
        notificationApi.error({
            key,
            message: title,
            description: content,
            duration: 4.5,
            placement: 'topRight',
        });
    }, [notificationApi]);

    const showSuccess = useCallback((title, description, key = 'reportSuccess') => {
        notificationApi.destroy(key);
        notificationApi.success({
            key,
            message: title,
            description,
            duration: 3,
            placement: 'topRight',
        });
    }, [notificationApi]);

    // Fetch contracts
    useEffect(() => {
        const fetchContracts = async () => {
            try {
                const response = await api.get('/api/contracts');
                if (response.data.success) {
                    setContracts(response.data.data || []);
                }
            } catch (error) {
                console.error('Error fetching contracts:', error);
                showError('Failed to Load', 'Could not load contracts.');
            }
        };
        fetchContracts();
    }, [showError]);

    // Fetch tickets filtered by contract
    const handleContractSelect = async (contractId) => {
        setSelectedContract(contractId);
        setTickets([]);
        setCrList([]);
        setReportData(null);
        form.setFieldsValue({ ticket_id: undefined, cr_master_id: undefined });

        if (!contractId) return;

        try {
            const response = await api.get('/api/tickets', {
                params: { contractId: contractId }
            });
            const ticketsData = response.data.data || [];
            
            const filteredTickets = ticketsData.filter(ticket => 
                ticket.contractId === parseInt(contractId)
            );
            setTickets(filteredTickets);
            
            if (filteredTickets.length === 0) {
                message.info('No tickets found for this contract.');
            }
        } catch (error) {
            console.error('Error fetching tickets:', error);
            showError('Failed to Load', 'Could not load tickets for this contract.');
        }
    };

    // Fetch CR/CMs when ticket is selected
    const handleTicketSelect = async (ticketId) => {
        form.setFieldsValue({ cr_master_id: undefined });
        setCrList([]);
        setReportData(null);

        if (!ticketId) return;

        try {
            const response = await api.get(`/api/time-tracking/cr/ticket/${ticketId}`);
            if (response.data.success) {
                setCrList(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching CR/CMs:', error);
            showError('Failed to Load', 'Could not load CR/CMs for this ticket.');
        }
    };

    // Proper date parsing function
    const parseDate = (dateStr) => {
        if (!dateStr || dateStr === 'N/A' || dateStr === '-') return null;
        
        // Try parsing as DD/MM/YYYY (already formatted)
        if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
            return dateStr;
        }
        
        // Try parsing as YYYY-MM-DD (ISO)
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
            const m = moment(dateStr);
            if (m.isValid()) {
                return m.format('DD/MM/YYYY');
            }
        }
        
        // Try parsing as full ISO string
        const m = moment(dateStr);
        if (m.isValid()) {
            return m.format('DD/MM/YYYY');
        }
        
        return 'Invalid Date';
    };

    // Generate report and show preview
    const handleGenerateReport = async (values) => {
        if (!values.cr_master_id) {
            message.warning('Please select a CR/CM');
            return;
        }

        setGenerating(true);
        try {
            const response = await api.get(`/api/time-tracking/report/single/${values.cr_master_id}`);
            
            if (response.data.success) {
                const data = response.data.data;
                
                // ✅ Get generated_by from backend
                let generatedByName = data.generated_by || 'System';
                
                // If backend returned "undefined undefined" or empty, try to extract from JWT
                if (generatedByName === 'undefined undefined' || generatedByName === 'System' || !generatedByName) {
                    const userData = user?.user || user;
                    const firstName = userData?.firstName || userData?.first_name || '';
                    const lastName = userData?.lastName || userData?.last_name || '';
                    const fullName = `${firstName} ${lastName}`.trim();
                    
                    if (fullName) {
                        generatedByName = fullName;
                    } else if (userData?.email) {
                        generatedByName = userData.email;
                    } else if (user?.email) {
                        generatedByName = user.email;
                    }
                }
                
                // ✅ Get ticket type short code from backend
                const ticketTypeShortCode = data.header?.ticket_type_short_code || 'REP';
                
                console.log('🔍 [Frontend] Backend generated_by:', data.generated_by);
                console.log('🔍 [Frontend] Using generated_by:', generatedByName);
                console.log('🔍 [Frontend] Ticket type short code:', ticketTypeShortCode);
                
                const formattedData = {
                    header: {
                        contract_name: data.header?.contract_name || '-',
                        ticket_uuid: data.header?.ticket_uuid || '-',
                        cr_number: data.header?.cr_number || '-',
                        request_date: parseDate(data.header?.request_date),
                        description: data.header?.description || '-',
                        target_production_date: parseDate(data.header?.target_production_date),
                        estimated_mandays: data.header?.estimated_mandays || '-',
                        impact_progress: data.header?.impact_progress || '-',
                        user_story_no: data.header?.user_story_no || '-',
                        ticket_type_name: data.header?.ticket_type_name || 'N/A',
                        ticket_type_short_code: ticketTypeShortCode
                    },
                    entries: data.entries?.map(entry => ({
                        ...entry,
                        entry_date: parseDate(entry.entry_date),
                        start_time: entry.start_time ? moment(entry.start_time, 'HH:mm:ss').format('hh:mm A') : '-',
                        end_time: entry.end_time ? moment(entry.end_time, 'HH:mm:ss').format('hh:mm A') : '-',
                        duration_display: entry.duration ? `${parseFloat(entry.duration).toFixed(2)} hrs` : '-',
                        activity_type: entry.activity_type || 'Uncategorized',
                        resource: entry.resource || '-',
                        description: entry.description || '-'
                    })),
                    total_hours: data.total_hours || 0,
                    total_hours_display: `${(data.total_hours || 0).toFixed(2)} hrs`,
                    man_days: data.man_days || 0,
                    man_days_display: `${data.man_days || 0} day${(data.man_days || 0) > 1 ? 's' : ''}`,
                    breakdown: data.breakdown || [],
                    report_title: data.header?.contract_name || 'CR/CM Report',
                    generated_by: generatedByName,
                    generated_date: data.generated_date || moment().tz('Asia/Kuala_Lumpur').format('DD/MM/YYYY HH:mm')
                };
                
                setReportData(formattedData);
                showSuccess('Report Generated', 'CR/CM report generated successfully.');
            } else {
                showError('Generation Failed', response.data.message || 'Failed to generate report.');
            }
        } catch (error) {
            console.error('Error generating report:', error);
            showError('Generation Failed', error.response?.data?.message || 'An error occurred while generating the report.');
        } finally {
            setGenerating(false);
        }
    };

    // Export to Word
    const exportToWord = async () => {
        if (!reportData) return;
        setExporting(true);
        try {
            const crMasterId = form.getFieldValue('cr_master_id');
            const response = await api.post('/api/reports/time-tracking/cr/single', {
                crMasterId: crMasterId,
                format: 'docx'
            }, {
                responseType: 'blob'
            });

            if (response.data instanceof Blob) {
                const url = window.URL.createObjectURL(response.data);
                const link = document.createElement('a');
                link.href = url;
                
                // ✅ Use ticket type short code from reportData
                const ticketTypeShortCode = reportData.header?.ticket_type_short_code || 'REP';
                const filePrefix = getFilePrefix(ticketTypeShortCode);
                
                const crNumber = reportData.header?.cr_number || 'report';
                // Remove any existing prefix from the cr_number
                let cleanCrNumber = crNumber.replace(/^(CR|CM|OTH|PM|REP)\s*/i, '');
                cleanCrNumber = cleanCrNumber.replace(/\s/g, '_').replace(/#/g, '');
                // If cleanCrNumber is empty, use '1'
                if (!cleanCrNumber || cleanCrNumber === 'report') {
                    cleanCrNumber = '1';
                }
                const filename = `${filePrefix}_${cleanCrNumber}_${moment().format('YYYYMMDD')}.docx`;
                
                console.log('📄 Filename:', filename);
                
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                showSuccess('Export Successful', 'Word document downloaded successfully.');
            }
        } catch (error) {
            console.error('Word export error:', error);
            showError('Export Failed', 'Failed to export Word document.');
        } finally {
            setExporting(false);
        }
    };

    // Export to PDF
    const exportToPDF = async () => {
        if (!reportData) return;
        setExporting(true);
        try {
            const crMasterId = form.getFieldValue('cr_master_id');
            const response = await api.post('/api/reports/time-tracking/cr/single', {
                crMasterId: crMasterId,
                format: 'pdf'
            }, {
                responseType: 'blob'
            });

            if (response.data instanceof Blob) {
                const url = window.URL.createObjectURL(response.data);
                const link = document.createElement('a');
                link.href = url;
                
                // ✅ Use ticket type short code from reportData
                const ticketTypeShortCode = reportData.header?.ticket_type_short_code || 'REP';
                const filePrefix = getFilePrefix(ticketTypeShortCode);
                
                const crNumber = reportData.header?.cr_number || 'report';
                // Remove any existing prefix from the cr_number
                let cleanCrNumber = crNumber.replace(/^(CR|CM|OTH|PM|REP)\s*/i, '');
                cleanCrNumber = cleanCrNumber.replace(/\s/g, '_').replace(/#/g, '');
                // If cleanCrNumber is empty, use '1'
                if (!cleanCrNumber || cleanCrNumber === 'report') {
                    cleanCrNumber = '1';
                }
                const filename = `${filePrefix}_${cleanCrNumber}_${moment().format('YYYYMMDD')}.pdf`;
                
                console.log('📄 Filename:', filename);
                
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                showSuccess('Export Successful', 'PDF downloaded successfully.');
            }
        } catch (error) {
            console.error('PDF export error:', error);
            showError('Export Failed', 'Failed to export PDF.');
        } finally {
            setExporting(false);
        }
    };

    // Export to Excel (CSV)
    const exportToExcel = () => {
        if (!reportData) return;
        setExporting(true);
        try {
            const excelData = [];
            
            // Header
            excelData.push(['CR/CM REPORT']);
            excelData.push([]);
            excelData.push(['CR/CM #', reportData.header?.cr_number || '-']);
            excelData.push(['Request Date', reportData.header?.request_date || '-']);
            excelData.push(['CR Description', reportData.header?.description || '-']);
            excelData.push(['LHDNM\'s Target Prod Date', reportData.header?.target_production_date || '-']);
            excelData.push(['Estimated Mandays', reportData.header?.estimated_mandays || '-']);
            excelData.push(['Impact / Progress', reportData.header?.impact_progress || '-']);
            excelData.push(['User Story No.', reportData.header?.user_story_no || '-']);
            excelData.push(['Ticket Number', reportData.header?.ticket_uuid || '-']);
            excelData.push([]);
            
            // Time Entries
            excelData.push(['TIME ENTRIES / ACTIVITIES']);
            excelData.push(['Date', 'Start Time', 'End Time', 'Activity Type', 'Resource(s)', 'Duration (hrs)', 'Description']);
            
            if (reportData.entries && reportData.entries.length > 0) {
                reportData.entries.forEach(entry => {
                    excelData.push([
                        entry.entry_date || '-',
                        entry.start_time || '-',
                        entry.end_time || '-',
                        entry.activity_type || '-',
                        entry.resource || '-',
                        entry.duration || 0,
                        entry.description || '-'
                    ]);
                });
            }
            
            excelData.push([]);
            excelData.push(['TOTAL HOURS', '', '', '', '', reportData.total_hours_display || '0 hrs', '']);
            excelData.push(['MAN DAYS', '', '', '', '', reportData.man_days_display || '0 days', '']);
            excelData.push([]);
            
            // Breakdown
            excelData.push(['BREAKDOWN BY ACTIVITY TYPE']);
            excelData.push(['Activity Type', 'Hours']);
            if (reportData.breakdown && reportData.breakdown.length > 0) {
                reportData.breakdown.forEach(item => {
                    excelData.push([item.type, item.hours]);
                });
            }
            
            const csvContent = excelData.map(row => 
                row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
            ).join('\n');
            
            const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // ✅ Use ticket type short code from reportData
            const ticketTypeShortCode = reportData.header?.ticket_type_short_code || 'REP';
            const filePrefix = getFilePrefix(ticketTypeShortCode);
            
            const crNumber = reportData.header?.cr_number || 'report';
            // Remove any existing prefix from the cr_number
            let cleanCrNumber = crNumber.replace(/^(CR|CM|OTH|PM|REP)\s*/i, '');
            cleanCrNumber = cleanCrNumber.replace(/\s/g, '_').replace(/#/g, '');
            // If cleanCrNumber is empty, use '1'
            if (!cleanCrNumber || cleanCrNumber === 'report') {
                cleanCrNumber = '1';
            }
            link.download = `${filePrefix}_${cleanCrNumber}_${moment().format('YYYYMMDD')}.csv`;
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            showSuccess('Export Successful', 'Excel file downloaded successfully.');
        } catch (error) {
            console.error('Excel export error:', error);
            showError('Export Failed', 'Failed to export Excel file.');
        } finally {
            setExporting(false);
        }
    };

    // Table columns with proper formatting
    const entryColumns = [
        {
            title: 'Date',
            dataIndex: 'entry_date',
            key: 'entry_date',
            width: 120,
            render: (text) => <Text strong>{text || '-'}</Text>
        },
        {
            title: 'Start Time',
            dataIndex: 'start_time',
            key: 'start_time',
            width: 100,
            render: (text) => text || '-'
        },
        {
            title: 'End Time',
            dataIndex: 'end_time',
            key: 'end_time',
            width: 100,
            render: (text) => text || '-'
        },
        {
            title: 'Activity Type',
            dataIndex: 'activity_type',
            key: 'activity_type',
            width: 160,
            render: (text) => <Tag color="cyan">{text || 'Uncategorized'}</Tag>
        },
        {
            title: 'Resource(s)',
            dataIndex: 'resource',
            key: 'resource',
            width: 180,
            render: (text) => text || '-'
        },
        {
            title: 'Duration',
            dataIndex: 'duration_display',
            key: 'duration_display',
            width: 100,
            align: 'center',
            render: (text) => <Text strong>{text || '-'}</Text>
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (text) => text || '-'
        }
    ];

    return (
        <div className="time-tracking-page">
            <div className="time-tracking-header">
                <Title level={2} className="time-tracking-title">
                    <FileTextOutlined /> CR/CM Activity Report
                </Title>
                <Text className="time-tracking-description">
                    Generate a detailed report for a single Change Request (CR) or Corrective Maintenance (CM).
                    Includes all activity entries, total hours, man days, and breakdown by activity type.
                </Text>
            </div>

            <Card className="time-tracking-card">
                <div className="time-tracking-content">
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleGenerateReport}
                    >
                        <Row gutter={16}>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    name="contract_id"
                                    label="Contract"
                                    rules={[{ required: true, message: 'Please select a contract' }]}
                                >
                                    <Select
                                        placeholder="Select contract"
                                        showSearch
                                        optionFilterProp="children"
                                        onChange={handleContractSelect}
                                        size="large"
                                    >
                                        {contracts.map(contract => (
                                            <Option key={contract.id} value={contract.id}>
                                                {contract.contractName || `Contract #${contract.id}`}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    name="ticket_id"
                                    label="Ticket / Project"
                                    rules={[{ required: true, message: 'Please select a ticket' }]}
                                >
                                    <Select
                                        placeholder="Select ticket"
                                        showSearch
                                        optionFilterProp="children"
                                        onChange={handleTicketSelect}
                                        disabled={!selectedContract || tickets.length === 0}
                                        size="large"
                                        notFoundContent={selectedContract ? "No tickets found for this contract" : "Please select a contract first"}
                                    >
                                        {tickets.map(ticket => (
                                            <Option key={ticket.id} value={ticket.id}>
                                                {ticket.ticketUuid} - {ticket.title}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    name="cr_master_id"
                                    label="CR/CM Number"
                                    rules={[{ required: true, message: 'Please select a CR/CM' }]}
                                >
                                    <Select
                                        placeholder="Select CR/CM"
                                        showSearch
                                        optionFilterProp="children"
                                        disabled={crList.length === 0}
                                        size="large"
                                        notFoundContent="No CR/CMs found for this ticket"
                                    >
                                        {crList.map(cr => (
                                            <Option key={cr.id} value={cr.id}>
                                                {cr.cr_number} - {cr.description?.substring(0, 50) || ''}...
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item>
                            <Space>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<EyeOutlined />}
                                    loading={generating}
                                    size="large"
                                >
                                    Generate Report
                                </Button>
                                <Button
                                    icon={<RollbackOutlined />}
                                    onClick={() => navigate(-1)}
                                    size="large"
                                >
                                    Back
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>

                    {/* Preview when reportData exists */}
                    {reportData && (
                        <>
                            <Divider />

                            <div className="report-preview">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                    <Title level={4} style={{ margin: 0 }}>
                                        <FileTextOutlined /> Report Preview
                                    </Title>
                                    <Space>
                                        <Tooltip title="Export as Word Document">
                                            <Button
                                                icon={<FileWordOutlined />}
                                                onClick={exportToWord}
                                                loading={exporting}
                                            >
                                                Word
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Export as PDF">
                                            <Button
                                                icon={<FilePdfOutlined />}
                                                onClick={exportToPDF}
                                                loading={exporting}
                                            >
                                                PDF
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Export as Excel">
                                            <Button
                                                icon={<FileExcelOutlined />}
                                                onClick={exportToExcel}
                                                loading={exporting}
                                            >
                                                Excel
                                            </Button>
                                        </Tooltip>
                                    </Space>
                                </div>

                                <div id="report-content" style={{
                                    background: '#fafafa',
                                    padding: '24px',
                                    borderRadius: '8px',
                                    border: '1px solid #e8e8e8'
                                }}>
                                    {/* Contract name */}
                                    <div style={{ textAlign: 'center', marginBottom: 24 }}>
                                        <Title level={3} style={{ margin: 0 }}>
                                            {reportData.header?.contract_name || 'CR/CM Report'}
                                        </Title>
                                    </div>

                                    {/* Two-column layout */}
                                    <div style={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: '1fr 1fr', 
                                        gap: '16px',
                                        background: '#f8f9fa',
                                        padding: '16px',
                                        borderRadius: '6px',
                                        borderLeft: '4px solid #1a73e8',
                                        marginBottom: 24
                                    }}>
                                        <div>
                                            <div style={{ marginBottom: 8 }}>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>Ticket Number</Text>
                                                <div><Text strong>{reportData.header?.ticket_uuid || '-'}</Text></div>
                                            </div>
                                            <div style={{ marginBottom: 8 }}>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>Change Request (CR #)</Text>
                                                <div><Text strong style={{ color: '#1890ff' }}>{reportData.header?.cr_number || '-'}</Text></div>
                                            </div>
                                            <div style={{ marginBottom: 8 }}>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>CR Description</Text>
                                                <div><Text strong>{reportData.header?.description || '-'}</Text></div>
                                            </div>
                                            <div>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>User Story No.</Text>
                                                <div><Text strong>{reportData.header?.user_story_no || '-'}</Text></div>
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ marginBottom: 8 }}>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>Request Date</Text>
                                                <div><Text strong>{reportData.header?.request_date || '-'}</Text></div>
                                            </div>
                                            <div style={{ marginBottom: 8 }}>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>LHDNM's Target Prod Date</Text>
                                                <div><Text strong>{reportData.header?.target_production_date || '-'}</Text></div>
                                            </div>
                                            <div style={{ marginBottom: 8 }}>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>Estimated Mandays</Text>
                                                <div><Text strong>{reportData.header?.estimated_mandays || '-'}</Text></div>
                                            </div>
                                            <div>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>Impact / Progress</Text>
                                                <div><Text strong>{reportData.header?.impact_progress || '-'}</Text></div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Time Entries Table */}
                                    <div style={{ marginBottom: 16 }}>
                                        <Text strong style={{ fontSize: '15px' }}>TIME ENTRIES / ACTIVITIES</Text>
                                    </div>
                                    {reportData.entries && reportData.entries.length > 0 ? (
                                        <Table
                                            columns={entryColumns}
                                            dataSource={reportData.entries}
                                            rowKey="id"
                                            pagination={false}
                                            size="small"
                                            bordered
                                            style={{ marginBottom: 16 }}
                                        />
                                    ) : (
                                        <Empty description="No time entries found" style={{ padding: '20px 0' }} />
                                    )}

                                    {/* Totals and Breakdown */}
                                    <div style={{
                                        background: '#e6f7ff',
                                        padding: '16px 20px',
                                        borderRadius: '8px',
                                        border: '1px solid #91d5ff',
                                        marginTop: 16,
                                        display: 'flex',
                                        justifyContent: 'space-around',
                                        flexWrap: 'wrap',
                                        gap: 16
                                    }}>
                                        <div style={{ textAlign: 'center' }}>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>Total Hours</Text>
                                            <div>
                                                <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                                                    {reportData.total_hours_display || '0 hrs'}
                                                </Text>
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>Man Days</Text>
                                            <div>
                                                <Text strong style={{ fontSize: '18px', color: '#fa8c16' }}>
                                                    {reportData.man_days_display || '0 days'}
                                                </Text>
                                            </div>
                                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                                (8 hours = 1 day, rounded up)
                                            </Text>
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>Breakdown</Text>
                                            <div style={{ marginTop: 4 }}>
                                                {reportData.breakdown && reportData.breakdown.length > 0 ? (
                                                    <Space size={4} wrap>
                                                        {reportData.breakdown.map((item, index) => (
                                                            <Tag key={index} color="blue">
                                                                {item.type}: {item.hours} hrs
                                                            </Tag>
                                                        ))}
                                                    </Space>
                                                ) : (
                                                    <Text type="secondary">No breakdown available</Text>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div style={{
                                        marginTop: 24,
                                        paddingTop: 16,
                                        borderTop: '1px solid #e8e8e8',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        fontSize: '12px',
                                        color: '#8c8c8c'
                                    }}>
                                        <span>Generated by: {reportData.generated_by || 'System'}</span>
                                        <span>Date: {reportData.generated_date || moment().tz('Asia/Kuala_Lumpur').format('DD/MM/YYYY HH:mm')}</span>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default ReportGeneratorSingle;